import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Modal, Tooltip } from "@mui/material";
import { Info } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";

import Styles from "./Settlement.module.css";
import { ErrorMessage } from "../../../../commons/Toast/ErrorMessage";
import ConfirmationModal from "../../../../commons/ConfirmationModal/ConfirmationModal";
import {
  finalSettlementMakerChecker,
  oneTimeSettlement,
  schedulePayouts,
  updateRepScheduleStatus,
  userFinalSettlement,
  userFinalSettlementGet,
  viewSettlement,
} from "../../../../Services/Schedule/schedule";
import Payouts from "../Payouts/Payouts";
import { formatSlotTime } from "../../../../helpers/dateFormat";
import { walletAccountApi } from "../../../../Services/Payments/PaymentApi";
import { convertDate } from "../../../../commons/FormatDate";
import ViewDoc from "../../ConGen/ViewDoc/ViewDoc";
import { ReactComponent as ViewIcon } from "../../../../assets/images/view.svg";

// ─── UI Helpers ───────────────────────────────────────────────────────────────
const formatINR = (n) => {
  if (n == null || n === "" || isNaN(Number(n))) return null;
  return Number(n).toLocaleString("en-IN", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
};

const getCheckerAmount = (ele) =>
  (ele?.checkerInputChargeAmount !== 0
    ? ele?.checkerInputChargeAmount
    : null) ??
  ele?.makerInputChargeAmount ??
  ele?.userInputChargeAmount;

const getMakerAmount = (ele) =>
  (ele?.makerInputChargeAmount !== 0 ? ele?.makerInputChargeAmount : null) ??
  ele?.userInputChargeAmount;

const getPaymentAmount = (ele) =>
  ele?.checkerInputChargeAmount ??
  ele?.makerInputChargeAmount ??
  ele?.userInputChargeAmount;

const getDisplayValue = (ele, role) => {
  const status = ele?.status;

  if (role === "MAKER") {
    if (status === "PAYMENT") return getPaymentAmount(ele);
    if (status === "CHECKER") return getCheckerAmount(ele);
    return ele?.makerInputChargeAmount || ele?.userInputChargeAmount;
  }

  if (role === "CHECKER") {
    return getCheckerAmount(ele);
  }

  if (status === "PAYMENT") return getPaymentAmount(ele);
  if (status === "CHECKER") return getCheckerAmount(ele);
  if (status === "MAKER") return getMakerAmount(ele);

  return ele?.userInputChargeAmount ?? ele?.amount;
};

// ─── PartyName ────────────────────────────────────────────────────────────────
const PartyName = ({ name, muted = false }) => {
  if (!name || name === "-" || name === "NA")
    return <span className={Styles.dash}>—</span>;
  return (
    <span className={muted ? Styles.partyMuted : Styles.party}>{name}</span>
  );
};

// ─── StatusPill ───────────────────────────────────────────────────────────────
const StatusPill = ({ status }) => {
  const cls =
    status === "approved"
      ? Styles.pillApproved
      : status === "rejected"
        ? Styles.pillRejected
        : Styles.pillPending;
  const label =
    status === "approved"
      ? "Approved"
      : status === "rejected"
        ? "Rejected"
        : "Pending";
  return (
    <span className={`${Styles.pill} ${cls}`}>
      <span className={Styles.pillDot} />
      {label}
    </span>
  );
};

// ─── SettlementTableHeader ────────────────────────────────────────────────────
const SettlementTableHeader = ({ role, hideBearerPayer }) => {
  const showVerify = role === "MAKER" || role === "CHECKER";
  const partiesColSpan = hideBearerPayer ? 2 : 4;
  return (
    <thead>
      <tr>
        <th className={`${Styles.th} ${Styles.stickyLeft}`} rowSpan={2}>
          Charge Type
        </th>
        <th
          className={`${Styles.grpHeader} ${Styles.sepStrong}`}
          colSpan={partiesColSpan}
        >
          Parties
        </th>
        <th className={`${Styles.grpHeader} ${Styles.sepStrong}`} colSpan={1}>
          System
        </th>
        <th className={`${Styles.grpHeader} ${Styles.sepStrong}`} colSpan={2}>
          Approval
        </th>
        <th
          className={`${Styles.th} ${Styles.thNum} ${Styles.sepStrong}`}
          rowSpan={2}
        >
          Final Amount (₹)
          <Tooltip title="Final amount as per delivered quantum." arrow>
            <Info className={Styles.infoIcon} />
          </Tooltip>
        </th>
        <th className={Styles.th} rowSpan={2}>
          Maker Comment
        </th>
        <th className={Styles.th} rowSpan={2}>
          Checker Comment
        </th>
        {showVerify && (
          <th
            className={`${Styles.th} ${Styles.thCtr} ${Styles.sepStrong} ${Styles.stickyRight}`}
            rowSpan={2}
          >
            Verify
          </th>
        )}
      </tr>
      <tr>
        <th className={`${Styles.th} ${Styles.sepStrong}`}>From User</th>
        <th className={Styles.th}>To User</th>
        {!hideBearerPayer && <th className={Styles.th}>Bearer</th>}
        {!hideBearerPayer && <th className={Styles.th}>Payer</th>}
        <th className={`${Styles.th} ${Styles.thNum} ${Styles.sepStrong}`}>
          Est. Amount (₹)
          <Tooltip title="Estimated amount as per schedule quantum." arrow>
            <Info className={Styles.infoIcon} />
          </Tooltip>
        </th>
        <th className={`${Styles.th} ${Styles.thCtr} ${Styles.sepStrong}`}>
          Maker
        </th>
        <th className={`${Styles.th} ${Styles.thCtr}`}>Checker</th>
      </tr>
    </thead>
  );
};

// ─── SettlementRow ────────────────────────────────────────────────────────────
const SettlementRow = ({
  ele,
  index,
  role,
  onAmountChange,
  onCheckerAction,
  onCommentChange,
  onResetRow,
  rawDamValue,
  type,
  hideBearerPayer,
}) => {
  const showVerify = role === "MAKER" || role === "CHECKER";
  const chargeName = ele?.name || ele?.chargeName || "—";
  const isFixed = (ele?.type || ele?.chargeType) !== "PER_MW";
  const chargeRate = isFixed
    ? "Fixed"
    : `₹${ele?.value || ele?.chargeValue?.toFixed(3) || 0}/kWh`;

  const getMakerStatus = () => {
    if (["CHECKER", "PAYMENT"].includes(ele?.status)) return "approved";
    if (ele?.status === "RECHECK") return "rejected";
    return "pending";
  };
  const getCheckerStatus = () => {
    if (ele?.status === "PAYMENT") return "approved";
    if (ele?.status === "RECHECK") return "rejected";
    return "pending";
  };

  const isReadOnly = (() => {
    if (role === "MAKER" && ["CHECKER", "PAYMENT"].includes(ele?.status))
      return true;
    if (role === "CHECKER") return true;
    if (
      (role === "CONSUMER" ||
        role === "GENERATOR" ||
        role === "SUPER_ADMIN" ||
        role === "ADMIN") &&
      ["MAKER", "CHECKER", "PAYMENT"].includes(ele?.status)
    )
      return true;
    return false;
  })();

  const isAmountReadOnly =
    isReadOnly || (ele?.isNonEditable && role === "CONSUMER");
  const displayValue = getDisplayValue(ele, role);

  // Maker: active when already verified (CHECKER status); Checker: active when fully approved
  const approveActive =
    role === "MAKER" ? ele?.status === "CHECKER" : ele?.status === "PAYMENT";
  const rejectActive = ele?.status === "RECHECK";

  // Maker can re-verify any row not yet fully completed (not PAYMENT)
  const canApprove =
    (role === "CHECKER" &&
      ele?.status !== "PAYMENT" &&
      ele?.status !== "RECHECK" &&
      type !== "HISTORY") ||
    (role === "MAKER" && ele?.status !== "PAYMENT" && type !== "HISTORY");

  const canReject =
    role === "CHECKER" &&
    ele?.status !== "RECHECK" &&
    ele?.status !== "PAYMENT" &&
    type !== "HISTORY";

  // Row tint based on checker decision
  const rowTint = approveActive
    ? Styles.rowApproved
    : rejectActive
      ? Styles.rowRejected
      : "";

  return (
    <tr className={`${Styles.tableRow} ${rowTint}`}>
      {/* Charge Type – sticky left, status left-border indicator */}
      <td className={`${Styles.td} ${Styles.stickyLeft} ${Styles.chargeCell}`}>
        <Tooltip title={chargeName} arrow>
          <span>{chargeName}</span>
        </Tooltip>
      </td>

      {/* From User */}
      <td className={`${Styles.td} ${Styles.sepStrong}`}>
        <PartyName name={ele?.fromUser} />
      </td>

      {/* To User – muted when same entity as From */}
      <td className={Styles.td}>
        <PartyName name={ele?.toUser} muted={ele?.toUser === ele?.fromUser} />
      </td>

      {/* Bearer – fallback to fromUserRole */}
      {!hideBearerPayer && (
        <td className={Styles.td}>
          <PartyName name={ele?.bearer || ele?.fromUserRole} />
        </td>
      )}

      {/* Payer – fallback to toUserRole */}
      {!hideBearerPayer && (
        <td className={Styles.td}>
          <PartyName name={ele?.payer || ele?.toUserRole} />
        </td>
      )}

      {/* Est. Amount */}
      <td className={`${Styles.td} ${Styles.sepStrong}`}>
        <div className={Styles.amtCell}>
          {ele?.amount != null || ele?.chargeAmount != null ? (
            <span className={Styles.numMono}>
              {formatINR(ele?.amount ?? ele?.chargeAmount) ?? "—"}
            </span>
          ) : (
            <span className={Styles.dash}>—</span>
          )}
          <span
            className={`${Styles.rateSub} ${isFixed ? Styles.rateFixed : ""}`}
          >
            {chargeRate}
          </span>
          {ele?.fromUser === "Consumer" &&
            ele?.toUser === "Generator" &&
            rawDamValue && (
              <span className={Styles.damValue}>{rawDamValue}</span>
            )}
        </div>
      </td>

      {/* Maker approval */}
      <td className={`${Styles.td} ${Styles.thCtr} ${Styles.sepStrong}`}>
        <StatusPill status={getMakerStatus()} />
      </td>

      {/* Checker approval */}
      <td className={`${Styles.td} ${Styles.thCtr}`}>
        <StatusPill status={getCheckerStatus()} />
      </td>

      {/* Final Amount – finbox design */}
      <td className={`${Styles.td} ${Styles.sepStrong}`}>
        {isAmountReadOnly ? (
          <div className={Styles.final}>
            <div className={`${Styles.finbox} ${Styles.finboxLock}`}>
              <span className={Styles.finboxNum}>
                {formatINR(displayValue) ?? "—"}
              </span>
            </div>
            <span className={Styles.hint}>🔒 auto</span>
          </div>
        ) : (
          <div className={Styles.final}>
            <div className={`${Styles.finbox} ${Styles.finboxEdit}`}>
              <input
                className={Styles.finboxInput}
                type="text"
                value={displayValue || ""}
                onChange={(e) => onAmountChange(e, index)}
                inputMode="decimal"
              />
            </div>
            <span className={Styles.hint}>↑ editable</span>
          </div>
        )}
      </td>

      {/* Maker Comment – editable for MAKER, read-only for others */}
      <td className={Styles.td}>
        {role === "MAKER" && !isReadOnly ? (
          <input
            className={`${Styles.commentInput} ${ele?.isMakerAmountEdited && !ele?.makerComment?.trim() ? Styles.req : ""}`}
            placeholder={
              ele?.isMakerAmountEdited ? "Required…" : "Add comment…"
            }
            value={ele?.makerComment || ""}
            onChange={(e) =>
              onCommentChange(index, "makerComment", e.target.value)
            }
            data-row-index={index}
            data-comment-type="maker"
          />
        ) : (
          <Tooltip
            title={ele?.makerComment || ""}
            arrow
            disableHoverListener={!ele?.makerComment}
          >
            <span
              className={`${Styles.cmtRo} ${!ele?.makerComment ? Styles.cmtRoEmpty : ""}`}
            >
              {ele?.makerComment || "—"}
            </span>
          </Tooltip>
        )}
      </td>

      {/* Checker Comment – editable for CHECKER, read-only for others */}
      <td className={Styles.td}>
        {role === "CHECKER" && type !== "HISTORY" ? (
          <input
            className={`${Styles.commentInput} ${!ele?.checkerComment?.trim() ? Styles.req : ""}`}
            placeholder="Required…"
            value={ele?.checkerComment || ""}
            onChange={(e) =>
              onCommentChange(index, "checkerComment", e.target.value)
            }
            data-row-index={index}
            data-comment-type="checker"
          />
        ) : (
          <Tooltip
            title={ele?.checkerComment || ""}
            arrow
            disableHoverListener={!ele?.checkerComment}
          >
            <span
              className={`${Styles.cmtRo} ${!ele?.checkerComment ? Styles.cmtRoEmpty : ""}`}
            >
              {ele?.checkerComment || "—"}
            </span>
          </Tooltip>
        )}
      </td>

      {/* Verify */}
      {showVerify && (
        <td className={`${Styles.td} ${Styles.stickyRight} ${Styles.thCtr}`}>
          <div className={Styles.verifyBtns}>
            {role === "CHECKER" && (
              <button
                className={`${Styles.verifyBtn} ${Styles.rejectBtn} ${
                  rejectActive ? Styles.active : ""
                } ${approveActive ? Styles.faded : ""}`}
                title="Reject this charge"
                onClick={() =>
                  canReject && onCheckerAction("reject", ele, index)
                }
                disabled={!canReject}
              >
                ✕
              </button>
            )}
            <button
              className={`${Styles.verifyBtn} ${Styles.approveBtn} ${
                approveActive ? Styles.active : ""
              } ${rejectActive ? Styles.faded : ""}`}
              title="Approve this charge"
              onClick={() =>
                canApprove && onCheckerAction("accept", ele, index)
              }
              disabled={!canApprove}
            >
              ✓
            </button>
          </div>
          {/* Reset – only for MAKER on already-verified rows */}
          {/* {role === "MAKER" && approveActive && (
            <button
              className={Styles.resetBtn}
              onClick={() => onResetRow(ele, index)}
              title="Reset this row"
            >
              ↺ Reset
            </button>
          )} */}
        </td>
      )}
    </tr>
  );
};

// ─── SettlementFooter ─────────────────────────────────────────────────────────
const SettlementFooter = ({
  role,
  status,
  settlementDetails,
  onAction,
  handleClose,
}) => {
  const isReadOnlyConsumer =
    (role === "CONSUMER" || role === "GENERATOR") &&
    ["MAKER", "CHECKER", "PAYMENT"].includes(status);
  const isReadOnlyMaker =
    role === "MAKER" && ["CHECKER", "PAYMENT"].includes(status);
  const isReadOnlyChecker = role === "CHECKER" && status === "PAYMENT";

  if (isReadOnlyConsumer || isReadOnlyMaker || isReadOnlyChecker) return null;

  const approvedCount = settlementDetails.filter(
    (e) => e?.status === "PAYMENT",
  ).length;
  const rejectedCount = settlementDetails.filter(
    (e) => e?.status === "RECHECK",
  ).length;
  const total = settlementDetails.length;
  const allApproved = total > 0 && approvedCount === total;
  const anyRejected = rejectedCount > 0;

  return (
    <div className={Styles.foot}>
      <div className={Styles.actions}>
        <button
          className={`${Styles.btn} ${Styles.btnGhost}`}
          onClick={handleClose}
        >
          Cancel
        </button>

        {(role === "CONSUMER" || role === "GENERATOR") &&
          settlementDetails?.length > 0 && (
            <button
              className={`${Styles.btn} ${Styles.btnPrimary}`}
              onClick={() => onAction("submit")}
            >
              Submit
            </button>
          )}

        {role === "MAKER" &&
          settlementDetails?.every((ele) => ele?.status === "CHECKER") && (
            <button
              className={`${Styles.btn} ${Styles.btnPrimary}`}
              onClick={() => onAction("submit")}
            >
              Submit
            </button>
          )}

        {role === "CHECKER" && (
          <>
            <button
              className={`${Styles.btn} ${Styles.btnDanger}`}
              disabled={!anyRejected}
              onClick={() =>
                anyRejected &&
                onAction(
                  settlementDetails.some((e) => e?.status === "RECHECK")
                    ? "transferToMaker"
                    : "rejectAll",
                )
              }
            >
              Reject ({rejectedCount})
            </button>
            <button
              className={`${Styles.btn} ${Styles.btnPrimary}`}
              disabled={!allApproved}
              onClick={() => allApproved && onAction("payout")}
            >
              Approve &amp; Submit ({approvedCount}/{total})
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ─── SummaryCell ──────────────────────────────────────────────────────────────
const SummaryCell = ({ label, value, mono = false, extra }) => (
  <div className={Styles.summaryCell}>
    <span className={Styles.summaryLbl}>{label}</span>
    <span className={mono ? Styles.summaryValMono : Styles.summaryVal}>
      {value}
    </span>
    {extra}
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const Settlement = ({
  open,
  handleClose,
  id,
  data,
  handleDeliveryStatus,
  admin,
  renderScheuleData,
  isLast,
  selectedScheduleId,
  paymentScreen = false,
  paymentData,
  heading,
  connection,
  type,
  chargeType,
}) => {
  const role = useSelector(
    (state) => state?.persistedReducer?.user?.activeRole,
  );
  const userId = useSelector(
    (state) => state?.persistedReducer?.user?.user?.userId,
  );

  const userData = useSelector((state) => state?.persistedReducer);
  const isTrader = userData?.roles?.includes("TRADER");

  const [settlementDetails, setSettlementDetails] = useState([]);
  const [activeRowData, setActiveRowData] = useState(null);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [summaryViewDocOpen, setSummaryViewDocOpen] = useState(false);
  const [summaryViewDocConnectionId, setSummaryViewDocConnectionId] =
    useState(null);
  const [confirmModal, setConfirmModal] = useState({
    status: false,
    data: "",
    note: "",
    type: null,
  });

  const rawDamValue = (() => {
    const price = data?.calculatedDamPrice || 0;
    const rate = data?.energyRate || 0;
    if (data?.energyRateType === "DAM_MINUS")
      return `DAM-${rate}(DAM=${(price + rate).toFixed(3)})`;
    if (data?.energyRateType === "DAM_PLUS")
      return `DAM+${rate}(DAM=${(price - rate).toFixed(3)})`;
    return null;
  })();

  // ── Derived display values ──────────────────────────────────────────────────
  const selfConsumerName =
    userData?.user?.consumers?.find(
      (consumer) => consumer.consumerId === data?.selfPartyEntityId,
    )?.consumerName || "NA";

  const selfGeneratorName =
    userData?.user?.generators?.find(
      (generator) => generator.generatorId === data?.selfPartyEntityId,
    )?.generatorName || "NA";

  const buyerName =
    role === "GENERATOR"
      ? data?.counterPartyEntityName
      : data?.selfCompanyName || selfConsumerName;

  const sellerName =
    role === "GENERATOR"
      ? data?.selfCompanyName || selfGeneratorName
      : data?.counterPartyEntityName;

  // State pill
  const visibleRows = settlementDetails.filter((e) => e?.isView);
  const approvedCount = visibleRows.filter(
    (e) => e?.status === "PAYMENT",
  ).length;
  const rejectedCount = visibleRows.filter(
    (e) => e?.status === "RECHECK",
  ).length;
  const totalRows = visibleRows.length;

  const stateClass = (() => {
    if (role === "CHECKER") {
      if (rejectedCount > 0) return "statePillRejected";
      if (totalRows > 0 && approvedCount === totalRows) return "statePillReady";
      return "statePillReview";
    }
    if (data?.status === "PAYMENT") return "statePillReady";
    if (data?.status === "RECHECK") return "statePillRejected";
    return "statePillReview";
  })();

  const stateText = (() => {
    if (role === "CHECKER") {
      if (rejectedCount > 0) return "Changes Requested";
      if (totalRows > 0 && approvedCount === totalRows)
        return "Ready to Submit";
      return "Awaiting Checker Approval";
    }
    if (data?.status === "PAYMENT") return "Approved";
    if (data?.status === "RECHECK") return "Changes Requested";
    if (data?.status === "CHECKER") return "Awaiting Checker Approval";
    return "Awaiting Maker Review";
  })();

  // ── Data Fetching ───────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      if (paymentScreen && paymentData?.contractId) {
        await handleOneTimeSettlement();
      } else if (
        paymentScreen ||
        admin ||
        ["MAKER", "CHECKER", "PAYMENT"].includes(data?.status)
      ) {
        await handleAdminGetSettlement();
      } else {
        await handleGetSettlement();
      }
    };
    fetchData();
  }, [id, data, admin, role]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGetSettlement = async () => {
    let res = await viewSettlement({
      id,
      connectionId: data?.bilateralConnectionId,
      isForOneDay: true,
      repScheduleId: data?.id,
    });
    if (res?.data?.code === 200) {
      if (role === "CONSUMER" || role === "TRADER") {
        const processedData = processConsumerViewData(
          res?.data?.data,
          data,
          role,
        ).filter(
          (ele) =>
            ele?.frequency !== "ONE_TIME" ||
            ele?.name === "Extra Margin" ||
            ele?.name === "Energy Charge" ||
            ele?.name === "Refund Request",
        );
        setSettlementDetails(processedData);
      }
    } else {
      ErrorMessage(res?.data?.error);
      handleClose();
    }
  };

  const normalizeAmount = (value) => {
    if (value === null || value === undefined || value === "") return "";
    const num = Number(value);
    return Number.isNaN(num) ? String(value).trim() : num.toFixed(3);
  };

  const handleAdminGetSettlement = async () => {
    let res = await userFinalSettlementGet({
      scheduleId: data?.scheduleId || paymentData?.schId,
      repScheduleId: selectedScheduleId || data?.id || paymentData?.repId,
    });
    if (res?.data?.code === 200) {
      const processed = processAdminViewData(res?.data?.data, role).map(
        (item) => ({
          ...item,
          _initialMakerInputChargeAmount:
            item?.makerInputChargeAmount ?? item?.userInputChargeAmount ?? "",
          isMakerAmountEdited: false,
        }),
      );
      setSettlementDetails(processed);
    }
  };

  const handleOneTimeSettlement = async () => {
    let res = await oneTimeSettlement({
      bCId: data?.bilateralContractId || paymentData?.contractId,
    });
    if (res?.data?.code === 200) {
      const processed = processAdminViewData(res?.data?.data, role).map(
        (item) => ({
          ...item,
          _initialMakerInputChargeAmount:
            item?.makerInputChargeAmount ?? item?.userInputChargeAmount ?? "",
          isMakerAmountEdited: false,
        }),
      );
      setSettlementDetails(processed);
    }
  };

  // ── Input Handlers ──────────────────────────────────────────────────────────
  const handleAmountChange = (e, index) => {
    const val = e.target.value;
    if (!/^\d*\.?\d{0,3}$/.test(val)) return;

    setSettlementDetails((prev) =>
      prev.map((ele, idx) => {
        if (idx !== index) return ele;
        if (role === "MAKER") {
          const initialVal = normalizeAmount(
            ele?._initialMakerInputChargeAmount,
          );
          const currentVal = normalizeAmount(val);
          const isInitialEmpty = initialVal === "" || initialVal === "0.000";
          const edited =
            initialVal !== currentVal || (isInitialEmpty && val !== "");
          return {
            ...ele,
            makerInputChargeAmount: val,
            isMakerAmountEdited: edited,
          };
        }
        if (role === "CHECKER")
          return { ...ele, checkerInputChargeAmount: val };
        return { ...ele, userInputChargeAmount: val };
      }),
    );
  };

  const handleInlineCommentChange = (index, field, value) => {
    setSettlementDetails((prev) =>
      prev.map((ele, idx) =>
        idx !== index ? ele : { ...ele, [field]: value },
      ),
    );
  };

  const handleCommentChange = (comment) => {
    setSettlementDetails((prev) =>
      prev.map((ele, idx) => {
        if (idx !== activeRowData?.index) return ele;
        if (role === "MAKER") return { ...ele, makerComment: comment, comment };
        if (role === "CHECKER")
          return { ...ele, checkerComment: comment, comment };
        return { ...ele, comment };
      }),
    );
  };

  // ── Action Handlers ─────────────────────────────────────────────────────────
  const handleCheckerAction = (actionType, rowData, index) => {
    setActiveRowData({ ...rowData, index });
    if (actionType === "reject") {
      setConfirmModal({
        status: true,
        type: "reject_row",
        data: "Do you want to reject this value?",
        note: "Once rejected, Maker will need to update the values again.",
      });
    } else if (actionType === "accept") {
      setConfirmModal({
        status: true,
        type: "accept_row",
        data: "Do you want to accept this value?",
        note:
          role === "CHECKER"
            ? "Once accepted, payment will be processed as per the final values and transferred through Bank NOCA."
            : "Once transferred, Checker will verify the amount.",
      });
    }
  };

  const handleFooterAction = (actionType) => {
    if (actionType === "submit") {
      setConfirmModal({
        status: true,
        type: "submit_all",
        data: "Do you want to transfer to maker-checker?",
        note: "After confirmation, the payment will be forwarded to the checker for approval. Upon approval, the amount will be transferred through Bank NOCA.",
      });
    } else if (actionType === "rejectAll") {
      setConfirmModal({
        status: true,
        type: "reject_all",
        data: "Do you want to reject the transaction?",
        note: "It will be transferred back to the maker for verification.",
      });
    } else if (actionType === "transferToMaker") {
      setConfirmModal({
        status: true,
        type: "transfer_maker",
        data: "Do you want to reject this transaction?",
        note: "It will be transferred back to the maker.",
      });
    } else if (actionType === "payout") {
      setPayoutOpen(true);
    }
  };

  // ── Submission Logic ────────────────────────────────────────────────────────
  const getWalletId = async ({ role: r, id: wId }) => {
    const res = await walletAccountApi({ role: r, id: wId });
    return res?.data?.code === 200
      ? (res?.data?.data?.walletAccounts?.[0]?.id ?? null)
      : null;
  };

  const submitSettlementData = async ({ isReject = false } = {}) => {
    let nextStatus =
      role === "MAKER" ? "CHECKER" : role === "CHECKER" ? "PAYMENT" : "MAKER";
    if (isReject) nextStatus = "MAKER";

    const statusRes = await updateRepScheduleStatus({
      id: data?.id,
      status: nextStatus,
    });
    if (statusRes?.data?.code !== 200)
      return ErrorMessage(statusRes?.data?.error);

    const [consumerWalletId, generatorWalletId] = await Promise.all([
      getWalletId({
        role: "CONSUMER",
        id: connection?.capacityConsumer?.consumerId,
      }),
      getWalletId({
        role: "GENERATOR",
        id: connection?.capacityGenerator?.generatorId,
      }),
    ]);

    const payload = prepareSubmissionPayload({
      details: settlementDetails,
      role,
      userId,
      scheduleId: data?.scheduleId,
      isReject,
      data,
      selectedScheduleId,
      consumerWalletId,
      generatorWalletId,
    });

    let res;
    if (role === "MAKER" || role === "CHECKER") {
      res = await finalSettlementMakerChecker(payload);
    } else {
      res = await userFinalSettlement(payload);
    }

    if (res?.data?.code === 200) {
      if (role === "CONSUMER") {
        handleDeliveryStatus({
          id: data?.id,
          scheduleId: data?.scheduleId,
          settlement: true,
          payment: true,
        });
      } else {
        renderScheuleData();
        handleAdminGetSettlement();
      }
      setConfirmModal({ status: false });
      handleClose();
    } else {
      ErrorMessage(res?.data?.error);
    }
  };

  const handlePayoutsSubmit = async ({ list }) => {
    const payload = list?.map((ele) => ({
      bilateralContractId: data?.bilateralContractId,
      fromUserId: ele.fromUserId,
      toUserId: ele.toUserId,
      transferAmount: ele.totalAmount,
      receiverType:
        ele?.toUser === ele?.fromUser
          ? "REFUND"
          : ele?.toUser === "Consumer"
            ? "CONSUMER"
            : ele?.toUser === "Generator"
              ? "GENERATOR"
              : "OTHERS",
      fromUser: ele?.fromUser,
      toUser: ele?.toUser,
      status: "PENDING",
      transactionType: ele?.transactionType,
      repScheduleId: ele?.repScheduleId,
      bearer: ele?.bearer,
      payer: ele?.payer,
      creditWalletAccountId: ele?.creditWalletAccountId,
      debitWalletAccountId: ele?.debitWalletAccountId,
    }));
    let res = await schedulePayouts(payload);
    if (res?.data?.code === 200) await submitSettlementData();
  };

  const handleLocalRowUpdate = (updateType) => {
    setSettlementDetails((prev) =>
      prev.map((ele, idx) => {
        if (idx !== activeRowData?.index) return ele;
        if (updateType === "accept_row") {
          return {
            ...ele,
            status: role === "MAKER" ? "CHECKER" : "PAYMENT",
            checkerInputChargeAmount:
              role === "CHECKER"
                ? ele.checkerInputChargeAmount || ele.makerInputChargeAmount
                : null,
            makerInputChargeAmount:
              role === "CHECKER"
                ? ele.checkerInputChargeAmount || ele.makerInputChargeAmount
                : ele.makerInputChargeAmount,
          };
        }
        if (updateType === "reject_row") {
          return {
            ...ele,
            status: "RECHECK",
            checkerInputChargeAmount: null,
            makerInputChargeAmount: null,
          };
        }
        if (updateType === "reset_row") {
          return {
            ...ele,
            status: "MAKER",
            makerInputChargeAmount:
              ele._initialMakerInputChargeAmount ??
              ele.userInputChargeAmount ??
              "",
            isMakerAmountEdited: false,
            makerComment: "",
          };
        }
        return ele;
      }),
    );
    setConfirmModal({ status: false, data: "", note: "", type: null });
  };

  const handleResetRow = (rowData, index) => {
    setActiveRowData({ ...rowData, index });
    setConfirmModal({
      status: true,
      type: "reset_row",
      data: "Reset this row?",
      note: "The verification, final amount, and comment for this charge will be cleared and returned to pending state.",
    });
  };

  const handleModalSubmit = () => {
    const { type: mType } = confirmModal;

    if (mType === "reject_row") {
      const activeRow = settlementDetails?.[activeRowData?.index];

      if (role === "CHECKER") {
        if (!activeRow?.checkerComment?.trim()) {
          // Close modal and focus checker comment input
          setConfirmModal({ status: false, data: "", note: "", type: null });
          setTimeout(() => {
            const commentInput = document.querySelector(
              `input[data-row-index="${activeRowData?.index}"][data-comment-type="checker"]`,
            );
            if (commentInput) {
              commentInput.focus();
              commentInput.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
            }
            ErrorMessage(
              "⚠️ Please add a comment explaining why you're rejecting this charge.",
            );
          }, 100);
          return;
        }
      }
    }

    // Validate MAKER comments when editing values
    if (mType === "accept_row" && role === "MAKER") {
      const activeRow = settlementDetails?.[activeRowData?.index];

      if (activeRow?.isMakerAmountEdited && !activeRow?.makerComment?.trim()) {
        // Close modal and focus maker comment input
        setConfirmModal({ status: false, data: "", note: "", type: null });
        setTimeout(() => {
          const commentInput = document.querySelector(
            `input[data-row-index="${activeRowData?.index}"][data-comment-type="maker"]`,
          );
          if (commentInput) {
            commentInput.focus();
            commentInput.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
          ErrorMessage("📝 Please add a comment for your edited value.");
        }, 100);
        return;
      }
    }

    // Validate MAKER comments for all edited rows on submit
    if (mType === "submit_all" && role === "MAKER") {
      const rowsWithEditedAmounts = settlementDetails.filter(
        (ele) => ele?.isMakerAmountEdited === true,
      );

      const firstRowWithMissingComment = rowsWithEditedAmounts.find(
        (ele) => !ele?.makerComment?.trim(),
      );

      if (firstRowWithMissingComment) {
        const missingCommentIndex = settlementDetails.indexOf(
          firstRowWithMissingComment,
        );
        // Close modal and focus first missing comment input
        setConfirmModal({ status: false, data: "", note: "", type: null });
        setTimeout(() => {
          const commentInput = document.querySelector(
            `input[data-row-index="${missingCommentIndex}"][data-comment-type="maker"]`,
          );
          if (commentInput) {
            commentInput.focus();
            commentInput.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
          ErrorMessage(
            "📝 Please add comments for all edited values before submitting.",
          );
        }, 100);
        return;
      }
    }

    if (
      mType === "reject_row" ||
      mType === "accept_row" ||
      mType === "reset_row"
    )
      return handleLocalRowUpdate(mType);
    if (mType === "transfer_maker")
      return submitSettlementData({ isReject: true });
    return submitSettlementData();
  };

  const handleOpenViewDoc = (connectionId) => {
    setSummaryViewDocConnectionId(connectionId);
    setSummaryViewDocOpen(true);
  };

  // ── Filtered / sorted rows ──────────────────────────────────────────────────
  const filteredRows = useMemo(
    () =>
      settlementDetails
        .map((ele, originalIndex) => ({ ele, originalIndex }))
        .filter(({ ele }) => {
          if (role === "MAKER" || role === "CHECKER") return true;
          if (!ele?.isView) return false;
          if (
            ele?.toUser === "Trader" &&
            !isTrader &&
            (ele?.chargeName || ele?.name) !== "Energy Charge"
          )
            return false;
          if (isTrader) return true;
          if (role === "CONSUMER")
            return ele?.fromUserRole === "CONSUMER" || ele?.bearer === "BUYER";
          if (role === "GENERATOR")
            return ele?.bearer === "SELLER" || ele?.payer === "SELLER";
          return true;
        })
        .sort((a, b) => {
          const aGna = (a.ele?.name || a.ele?.chargeName)?.endsWith("_GNA");
          const bGna = (b.ele?.name || b.ele?.chargeName)?.endsWith("_GNA");
          if (aGna === bGna) return 0;
          return aGna ? -1 : 1;
        }),
    [settlementDetails, role, isTrader],
  );

  const hideBearerPayer =
    chargeType === "recurring" && (role === "CONSUMER" || role === "GENERATOR");

  // Time range display
  const timeRange =
    data?.type === "ONE_TIME"
      ? "-"
      : `${data?.fromBlock?.timeBlock?.split("-")[0]} – ${
          data?.toBlock?.timeBlock?.split("-")[1]?.trim() === "23:59:59"
            ? "24:00"
            : formatSlotTime(data?.toBlock?.timeBlock?.split("-")?.[1]?.trim())
        }`;

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <>
      {payoutOpen && (
        <Payouts
          open={payoutOpen}
          handleClose={() => setPayoutOpen(false)}
          list={settlementDetails}
          handlePayoutsSubmit={handlePayoutsSubmit}
        />
      )}

      {confirmModal.status && (
        <ConfirmationModal
          open={confirmModal.status}
          data={confirmModal.data}
          note={confirmModal.note || data?.note}
          header="Confirmation"
          handleSubmit={handleModalSubmit}
          handleClose={() => setConfirmModal({ status: false })}
          handleCommentChange={handleCommentChange}
          commentBox={confirmModal.type === "reject_row"}
          commentInit={confirmModal.type === "reject_row"}
        />
      )}

      {summaryViewDocOpen && (
        <ViewDoc
          open={summaryViewDocOpen}
          handleClose={() => setSummaryViewDocOpen(false)}
          connectionId={summaryViewDocConnectionId}
          viewDocData={data}
        />
      )}

      <Modal open={open} onClose={handleClose}>
        <div className={Styles.modal}>
          {/* ── Header ── */}
          <div className={Styles.head}>
            <div className={Styles.titleWrap}>
              <h1 className={Styles.modalTitle}>
                {heading || "Transaction Detail"}
              </h1>
              <div className={Styles.crumbs}>
                {!paymentScreen && data?.bilateralContractUniqueId && (
                  <span className={Styles.cid}>
                    {data.bilateralContractUniqueId}
                  </span>
                )}
                <span className={`${Styles.statePill} ${Styles[stateClass]}`}>
                  <span className={Styles.stateDot} />
                  {stateText}
                </span>
              </div>
            </div>
            <div className={Styles.headActions}>
              {(role === "MAKER" || role === "CHECKER") && (
                <button
                  className={Styles.docsBtn}
                  onClick={() => handleOpenViewDoc(data?.bilateralContractId)}
                >
                  <ViewIcon style={{ width: 14, height: 14 }} />
                  View Docs
                </button>
              )}
              <button
                className={Styles.closeBtn}
                aria-label="Close"
                onClick={handleClose}
              >
                <CloseIcon style={{ fontSize: 16 }} />
              </button>
            </div>
          </div>

          {/* ── Summary bar ── */}
          {!paymentScreen && (
            <div className={Styles.summary}>
              <SummaryCell
                label="Buyer → Seller"
                value={
                  <>
                    <b>{buyerName}</b>
                    <span className={Styles.summaryArrow}>→</span>
                    <b>{sellerName}</b>
                  </>
                }
              />
              {data?.fromDate && (
                <SummaryCell
                  label="Delivery Date"
                  value={convertDate(data?.fromDate) || "-"}
                />
              )}
              <SummaryCell label="Time Range" value={timeRange} mono />
              <SummaryCell
                label="Scheduled Volume"
                mono
                value={
                  data?.type === "ONE_TIME" ? (
                    "-"
                  ) : (
                    <>
                      {data?.scheduleVolume?.toFixed(2)} <small>MWh</small>
                    </>
                  )
                }
              />
              <SummaryCell
                label="Delivered Volume"
                mono
                value={
                  data?.type === "ONE_TIME" ? (
                    "-"
                  ) : (
                    <>
                      {data?.deliveredVolume?.toFixed(2)} <small>MWh</small>
                    </>
                  )
                }
                extra={
                  data?.type !== "ONE_TIME" &&
                  data?.scheduleVolume > 0 && (
                    <>
                      <div className={Styles.progBar}>
                        <div
                          className={Styles.progBarFill}
                          style={{
                            width: `${Math.min(100, Math.round((data?.deliveredVolume / data?.scheduleVolume) * 100))}%`,
                          }}
                        />
                      </div>
                      <span className={Styles.progTxt}>
                        {Math.round(
                          (data?.deliveredVolume / data?.scheduleVolume) * 100,
                        )}
                        % of scheduled
                      </span>
                    </>
                  )
                }
              />
              <SummaryCell
                label="Contract ID"
                value={data?.bilateralContractUniqueId || "NA"}
                mono
              />
            </div>
          )}

          {/* ── Table ── */}
          <div className={Styles.tableWrap}>
            <div className={Styles.tableScroll}>
              <table className={Styles.table}>
                <SettlementTableHeader
                  role={role}
                  hideBearerPayer={hideBearerPayer}
                />
                <tbody>
                  {console.debug("filteredRows", filteredRows)}
                  {filteredRows.map(({ ele, originalIndex }) => (
                    <SettlementRow
                      key={originalIndex}
                      index={originalIndex}
                      ele={ele}
                      role={role}
                      onAmountChange={handleAmountChange}
                      onCheckerAction={handleCheckerAction}
                      onCommentChange={handleInlineCommentChange}
                      onResetRow={handleResetRow}
                      rawDamValue={rawDamValue}
                      type={type}
                      hideBearerPayer={hideBearerPayer}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Footer ── */}
          {!paymentScreen && (
            <SettlementFooter
              role={role}
              status={data?.status}
              settlementDetails={settlementDetails}
              onAction={handleFooterAction}
              handleClose={handleClose}
            />
          )}
        </div>
      </Modal>
    </>
  );
};

export default Settlement;

// ─── Utility Functions (pure logic – unchanged) ───────────────────────────────

const processConsumerViewData = (responseData, scheduleData, role) => {
  const common = {
    scheduleId: scheduleData?.scheduleId,
    makerId: null,
    checkerId: null,
    actualChargeAmount: null,
    makerInputChargeAmount: null,
    checkerInputChargeAmount: null,
    isView: true,
  };

  const createCharge = (base, overrides) => ({
    ...base,
    ...common,
    ...overrides,
  });

  // If any generator charge routes to Trader, Energy Charge goes Consumer→Trader
  // and a Seller Amount row is added for Trader→Generator
  const traderGenCharges = (
    responseData?.generatorChargesBreakDown || []
  ).filter((charge) => charge?.chargeType === "Trader");
  const hasTraderCharge = traderGenCharges.length > 0;

  const energyChargeUserInput = (
    (responseData?.totalAmountToGenerator * scheduleData?.deliveredVolume) /
    scheduleData?.scheduleVolume
  )?.toFixed(3);

  const traderEntityId = traderGenCharges[0]?.toEntityId;

  const genDetails = createCharge({
    fromUser: responseData?.consumerName || "Consumer",
    toUser: hasTraderCharge
      ? "Trader"
      : responseData?.generatorName || "Generator",
    fromUserRole: "CONSUMER",
    toUserRole: hasTraderCharge ? "Trader" : "GENERATOR",
    // When routing to Trader, set both IDs explicitly
    ...(hasTraderCharge && {
      fromUserId: scheduleData?.selfPartyEntityId,
      toUserId: traderEntityId,
    }),
    name: "Energy Charge",
    frequency: "RECURRING",
    type: "PER_MW",
    value:
      scheduleData?.calculatedDamPrice?.toFixed(3) || scheduleData?.energyRate,
    isAdminCharge: false,
    amount: responseData?.totalAmountToGenerator?.toFixed(3),
    userInputChargeAmount: energyChargeUserInput,
  });

  // Seller Amount = Energy Charge − all Trader charges (Trader → Generator)
  let sellerAmountCharge = null;
  if (hasTraderCharge) {
    const traderTotalAmount = traderGenCharges.reduce(
      (acc, c) => acc + (parseFloat(c?.amount) || 0),
      0,
    );
    const traderTotalUserInput = traderGenCharges.reduce((acc, c) => {
      const val =
        c?.type === "FIXED"
          ? parseFloat(c?.amount) || 0
          : (parseFloat(c?.amount) * scheduleData?.deliveredVolume) /
              scheduleData?.scheduleVolume || 0;
      return acc + val;
    }, 0);

    const sellerAmt =
      (parseFloat(responseData?.totalAmountToGenerator) || 0) -
      traderTotalAmount;
    const sellerAmtUserInput =
      (parseFloat(energyChargeUserInput) || 0) - traderTotalUserInput;

    sellerAmountCharge = createCharge(
      {
        fromUser: "Trader",
        toUser: responseData?.generatorName || "Generator",
        fromUserRole: "Trader",
        toUserRole: "GENERATOR",
        // Trader → Generator: fromUserId is trader, toUserId is generator (counterParty)
        fromUserId: traderEntityId,
        toUserId: scheduleData?.counterPartyEntityId,
        name: "Seller Amount",
        frequency: "RECURRING",
        type: "FIXED",
        value: 0,
        isAdminCharge: false,
        amount: sellerAmt?.toFixed(3),
        userInputChargeAmount: sellerAmtUserInput?.toFixed(3),
        isNonEditable: true,
      },
      { isView: true },
    );
  }

  const processedCharges = (responseData?.consumerChargesBreakDown || []).map(
    (charge) =>
      createCharge(charge, {
        fromUser: responseData?.consumerName || "Consumer",
        toUser: charge?.chargeType,
        fromUserRole: "CONSUMER",
        toUserRole: charge?.chargeType || "OTHERS",
        isNonEditable: charge?.isAdminCharge,
        userInputChargeAmount:
          charge?.type === "FIXED"
            ? charge?.amount
            : (
                (charge?.amount * scheduleData?.deliveredVolume) /
                scheduleData?.scheduleVolume
              )?.toFixed(3),
      }),
  );

  const processedChargesGen = (responseData?.generatorChargesBreakDown || [])
    .filter((charge) => charge?.chargeType !== "Trader")
    .map((charge) =>
      createCharge(charge, {
        fromUser: responseData?.generatorName || "Generator",
        toUser: charge?.chargeType,
        fromUserRole: "GENERATOR",
        toUserRole: charge?.chargeType || "OTHERS",
        isNonEditable: charge?.isAdminCharge,
        userInputChargeAmount:
          charge?.type === "FIXED"
            ? charge?.amount
            : (
                (charge?.amount * scheduleData?.deliveredVolume) /
                scheduleData?.scheduleVolume
              )?.toFixed(3),
        isView: false,
      }),
    );

  // Trader charges — visible rows showing what the Trader retains from the energy charge
  const processedTraderCharges = hasTraderCharge
    ? traderGenCharges.map((charge) =>
        createCharge(charge, {
          fromUser: responseData?.consumerName || "Consumer",
          toUser: "Trader",
          fromUserRole: "CONSUMER",
          toUserRole: "Trader",
          fromUserId: scheduleData?.selfPartyEntityId,
          toUserId: traderEntityId,
          isNonEditable: charge?.isAdminCharge,
          userInputChargeAmount:
            charge?.type === "FIXED"
              ? charge?.amount
              : (
                  (charge?.amount * scheduleData?.deliveredVolume) /
                  scheduleData?.scheduleVolume
                )?.toFixed(3),
          isView: role === "TRADER",
        }),
      )
    : [];

  const calculateRefund = (items) =>
    items.reduce((acc, item) => {
      const original = item.amount || 0;
      const calculated = item.userInputChargeAmount || 0;
      return acc + (original - calculated);
    }, 0);

  const consumerRefundTotal = calculateRefund([
    genDetails,
    ...processedCharges,
  ]);
  const generatorRefundTotal = calculateRefund(processedChargesGen);

  const refund = createCharge({
    fromUser: responseData?.consumerName || "Consumer",
    toUser: responseData?.consumerName || "Consumer",
    fromUserRole: "CONSUMER",
    tpToUserRole: "CONSUMER",
    name: "Refund Request",
    frequency: "RECURRING",
    type: "FIXED",
    amount: 0,
    userInputChargeAmount: consumerRefundTotal?.toFixed(3),
    isNonEditable: true,
  });

  const refundGen = createCharge(refund, {
    fromUser: responseData?.generatorName || "Generator",
    toUser: responseData?.generatorName || "Generator",
    fromUserRole: "GENERATOR",
    tpToUserRole: "GENERATOR",
    name: "Refund Request",
    isView: false,
    amount: generatorRefundTotal?.toFixed(3),
    userInputChargeAmount: generatorRefundTotal?.toFixed(3),
    isNonEditable: true,
  });

  const refundConMargin = createCharge({
    fromUser: responseData?.consumerName || "Consumer",
    toUser: responseData?.consumerName || "Consumer",
    fromUserRole: "CONSUMER",
    tpToUserRole: "CONSUMER",
    name: "Extra Margin",
    frequency: "RECURRING",
    type: "FIXED",
    amount: responseData?.consumerRefundAmount?.toFixed(3) || 0,
    userInputChargeAmount: responseData?.consumerRefundAmount?.toFixed(3) || 0,
    isNonEditable: true,
  });

  const refundGenMargin = createCharge(refund, {
    fromUser: responseData?.generatorName || "Generator",
    toUser: responseData?.generatorName || "Generator",
    fromUserRole: "GENERATOR",
    tpToUserRole: "GENERATOR",
    name: "Extra Margin",
    isView: false,
    amount: responseData?.generatorRefundAmount?.toFixed(3) || 0,
    userInputChargeAmount: responseData?.generatorRefundAmount?.toFixed(3) || 0,
    isNonEditable: true,
  });

  return [
    genDetails,
    ...(sellerAmountCharge ? [sellerAmountCharge] : []),
    ...processedTraderCharges,
    ...processedCharges,
    refundConMargin,
    refund,
    ...processedChargesGen,
    refundGen,
    refundGenMargin,
  ];
};

const processAdminViewData = (charges, role) => {
  if (!charges) return [];
  return charges.map((charge) => {
    let updatedCharge = { ...charge, isView: true };
    if (role === "MAKER") {
      updatedCharge.makerInputChargeAmount =
        charge?.makerInputChargeAmount !== 0
          ? charge?.makerInputChargeAmount
          : charge?.userInputChargeAmount || 0;
    } else if (role === "CHECKER") {
      updatedCharge.checkerInputChargeAmount =
        charge?.checkerInputChargeAmount !== 0
          ? charge?.checkerInputChargeAmount
          : charge?.makerInputChargeAmount || 0;
    }
    return updatedCharge;
  });
};

const prepareSubmissionPayload = ({
  details,
  role,
  userId,
  scheduleId,
  isReject = false,
  data,
  selectedScheduleId,
  consumerWalletId,
  generatorWalletId,
}) => {
  return details.map((ele) => {
    const isMaker = role === "MAKER";
    const isChecker = role === "CHECKER";

    let status = "MAKER";
    if (isReject) status = "MAKER";
    else if (isMaker) status = "CHECKER";
    else if (isChecker) status = "PAYMENT";

    const selfId = data?.selfPartyEntityId;
    const counterId = data?.counterPartyEntityId;

    let fromUserId = ele?.fromUserRole === "CONSUMER" ? selfId : counterId;
    let toUserId;
    if (ele?.fromUserRole === "CONSUMER" && ele?.toUserRole === "GENERATOR") {
      toUserId = ele?.fromUserRole === "CONSUMER" ? counterId : selfId;
    } else if (ele?.fromUserRole === ele?.toUserRole) {
      toUserId = ele?.fromUserRole === "CONSUMER" ? selfId : counterId;
    } else {
      toUserId = ele?.toEntityId;
    }

    const resolvedFromUserId = ele?.fromUserId || fromUserId;
    const resolvedToUserId =
      ele?.fromUser === ele?.toUser
        ? resolvedFromUserId
        : ele?.toUserId || ele?.toEntityId || toUserId;

    return {
      id: isMaker || isChecker ? ele?.id : null,
      fromUser: ele?.fromUser,
      fromUserId: resolvedFromUserId,
      toUser: ele?.toUser,
      toUserId: resolvedToUserId,
      chargeName: ele?.name || ele?.chargeName,
      chargeFrequency: ele?.frequency || ele?.chargeFrequency,
      chargeType: ele?.type || ele?.chargeType,
      isAdminCharge: ele?.isAdminCharge || false,
      chargeAmount:
        ele?.amount || ele?.chargeAmount || ele?.userInputChargeAmount || 0,
      scheduleId,
      chargeValue: ele?.value || ele?.chargeValue || 0,
      makerId: isMaker || ele?.makerId ? ele?.makerId || userId : null,
      checkerId: isChecker || ele?.checkerId ? ele?.checkerId || userId : null,
      actualChargeAmount: ele?.actualChargeAmount || ele?.amount,
      userInputChargeAmount: ele?.userInputChargeAmount || 0,
      makerInputChargeAmount: ele?.makerInputChargeAmount,
      checkerInputChargeAmount: isMaker ? 0 : ele?.checkerInputChargeAmount,
      status,
      repScheduleId: selectedScheduleId || ele?.repScheduleId,
      makerComment: ele?.makerComment,
      checkerComment: ele?.checkerComment,
      bearer:
        ele?.bearer ||
        (ele?.fromUserRole === "CONSUMER"
          ? "BUYER"
          : ele?.fromUserRole === "GENERATOR"
            ? "SELLER"
            : "APPLICANT"),
      payer:
        ele?.payer ||
        (ele?.toUserRole === "CONSUMER"
          ? "BUYER"
          : ele?.toUserRole === "GENERATOR"
            ? "SELLER"
            : "APPLICANT"),
      creditWalletAccountId:
        ele?.creditWalletAccountId ||
        (ele?.toUserRole === "CONSUMER"
          ? consumerWalletId
          : ele?.toUserRole === "GENERATOR"
            ? generatorWalletId
            : null),
      debitWalletAccountId:
        ele?.debitWalletAccountId ||
        (ele?.fromUserRole === "CONSUMER"
          ? consumerWalletId
          : ele?.fromUserRole === "GENERATOR"
            ? generatorWalletId
            : null),
    };
  });
};
